import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ShieldCheck,
    LogOut,
    Loader2,
    Trash2,
    Plus,
    Save,
    KeyRound,
    CheckCircle2,
    AlertTriangle,
    ClipboardList,
    Copy,
} from "lucide-react";
import PageShell from "../components/PageShell";

const API = process.env.REACT_APP_BACKEND_URL;
const MAIN_FORM_ID = "staff-application";
const DEFAULT_IDS = ["roblox_nick", "discord_user", "idade", "disponibilidade", "experiencia", "motivacao"];
const TYPE_LABELS = { text: "Texto curto", number: "Número", textarea: "Texto longo" };

function Toggle({ checked, onChange, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            aria-pressed={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center overflow-hidden rounded-full transition-colors duration-200 ${
                checked ? "bg-violet-500" : "bg-white/15"
            }`}
        >
            <span
                className="h-5 w-5 rounded-full bg-white shadow transition-transform duration-200"
                style={{ transform: checked ? "translateX(22px)" : "translateX(2px)" }}
            />
        </button>
    );
}

function formatError(detail) {
    if (detail == null) return "Erro ao salvar. Tente novamente.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg : String(e))).join(" ");
    return String(detail);
}

export default function AdminPanel() {
    const navigate = useNavigate();
    const [forms, setForms] = useState(null);
    const [selectedId, setSelectedId] = useState(MAIN_FORM_ID);
    const [adminEmail, setAdminEmail] = useState("");
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState({ type: "", text: "" });
    const [newFormName, setNewFormName] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [newType, setNewType] = useState("text");
    const [pwd, setPwd] = useState({ current: "", next: "" });
    const [pwdNotice, setPwdNotice] = useState({ type: "", text: "" });

    const headers = useCallback(
        () => ({ Authorization: `Bearer ${localStorage.getItem("ebh1_admin_token")}` }),
        []
    );

    const logout = useCallback(() => {
        localStorage.removeItem("ebh1_admin_token");
        navigate("/admin");
    }, [navigate]);

    const loadForms = useCallback(async () => {
        const res = await axios.get(`${API}/api/admin/forms`, { headers: headers() });
        setForms(res.data);
        if (res.data.length && !res.data.find((f) => f.id === selectedId)) {
            setSelectedId(res.data[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    useEffect(() => {
        const token = localStorage.getItem("ebh1_admin_token");
        if (!token) {
            navigate("/admin");
            return;
        }
        (async () => {
            try {
                const me = await axios.get(`${API}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAdminEmail(me.data.email);
                await loadForms();
            } catch {
                logout();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = forms?.find((f) => f.id === selectedId);

    const saveForm = async (form, msg = "Alterações salvas com sucesso.") => {
        setSaving(true);
        setNotice({ type: "", text: "" });
        try {
            await axios.put(
                `${API}/api/forms/${form.id}`,
                { name: form.name, open: form.open, questions: form.questions, webhook_url: form.webhook_url || "" },
                { headers: headers() }
            );
            setForms((fs) => fs.map((f) => (f.id === form.id ? form : f)));
            setNotice({ type: "ok", text: msg });
        } catch (err) {
            if (err.response?.status === 401) return logout();
            setNotice({ type: "err", text: formatError(err.response?.data?.detail) });
        } finally {
            setSaving(false);
        }
    };

    const patchSelected = (patch) =>
        setForms((fs) => fs.map((f) => (f.id === selectedId ? { ...f, ...patch } : f)));

    const updateQuestion = (qid, patch) =>
        patchSelected({
            questions: selected.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)),
        });

    const createForm = async () => {
        if (newFormName.trim().length < 2) return;
        try {
            const res = await axios.post(
                `${API}/api/forms`,
                { name: newFormName.trim() },
                { headers: headers() }
            );
            setForms((fs) => [...fs, res.data]);
            setSelectedId(res.data.id);
            setNewFormName("");
            setNotice({ type: "ok", text: `Formulário "${res.data.name}" criado (inicia fechado).` });
        } catch (err) {
            if (err.response?.status === 401) return logout();
            setNotice({ type: "err", text: formatError(err.response?.data?.detail) });
        }
    };

    const deleteForm = async (id) => {
        if (!window.confirm("Remover este formulário? Esta ação não pode ser desfeita.")) return;
        try {
            await axios.delete(`${API}/api/forms/${id}`, { headers: headers() });
            const rest = forms.filter((f) => f.id !== id);
            setForms(rest);
            setSelectedId(rest[0]?.id || MAIN_FORM_ID);
        } catch (err) {
            if (err.response?.status === 401) return logout();
            setNotice({ type: "err", text: formatError(err.response?.data?.detail) });
        }
    };

    const addQuestion = () => {
        if (newLabel.trim().length < 2) return;
        patchSelected({
            questions: [
                ...selected.questions,
                { id: `custom_${Date.now()}`, label: newLabel.trim(), placeholder: "", type: newType, required: true, enabled: true },
            ],
        });
        setNewLabel("");
    };

    const changePassword = async (e) => {
        e.preventDefault();
        setPwdNotice({ type: "", text: "" });
        try {
            await axios.put(
                `${API}/api/auth/password`,
                { current_password: pwd.current, new_password: pwd.next },
                { headers: headers() }
            );
            setPwd({ current: "", next: "" });
            setPwdNotice({ type: "ok", text: "Senha alterada com sucesso." });
        } catch (err) {
            if (err.response?.status === 401) return logout();
            setPwdNotice({ type: "err", text: formatError(err.response?.data?.detail) });
        }
    };

    if (!forms) {
        return (
            <PageShell testid="page-admin-painel">
                <div className="flex items-center justify-center py-24 text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell testid="page-admin-painel">
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="eyebrow">Administração</p>
                    <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white">
                        Painel de Formulários
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Logado como <span className="text-gray-300">{adminEmail}</span>
                    </p>
                </div>
                <button
                    data-testid="admin-logout-button"
                    onClick={logout}
                    className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-bold text-gray-300 transition-colors hover:bg-white/10"
                >
                    <LogOut className="h-4 w-4" /> Sair
                </button>
            </div>

            {notice.text && (
                <div
                    data-testid="admin-save-notice"
                    className={`mb-6 flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium ${
                        notice.type === "ok"
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                            : "border-red-500/30 bg-red-500/10 text-red-300"
                    }`}
                >
                    {notice.type === "ok" ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
                    {notice.text}
                </div>
            )}

            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
                <div className="space-y-5">
                    <div className="glass-card rounded-2xl p-6">
                        <h2 className="font-display text-lg font-bold text-white">Meus formulários</h2>
                        <p className="mt-1 text-xs text-gray-500">
                            Selecione um formulário para editar. O botão liga/desliga libera ou fecha as respostas.
                        </p>
                        <div className="mt-4 space-y-2">
                            {forms.map((f) => (
                                <div
                                    key={f.id}
                                    data-testid={`form-row-${f.id}`}
                                    className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                                        f.id === selectedId
                                            ? "border-violet-500/40 bg-violet-500/10"
                                            : "border-white/10 bg-white/[0.03]"
                                    }`}
                                >
                                    <button
                                        data-testid={`form-select-${f.id}`}
                                        onClick={() => setSelectedId(f.id)}
                                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                    >
                                        <ClipboardList className="h-4 w-4 shrink-0 text-violet-400" />
                                        <span className="truncate text-sm font-semibold text-gray-100">{f.name}</span>
                                    </button>
                                    <button
                                        data-testid={`form-copy-link-${f.id}`}
                                        title="Copiar link público"
                                        onClick={() => {
                                            navigator.clipboard?.writeText(`${window.location.origin}/formulario/${f.id}`);
                                            setNotice({ type: "ok", text: "Link público copiado." });
                                        }}
                                        className="text-gray-500 transition-colors hover:text-violet-300"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                    <Toggle
                                        testid={`form-open-toggle-${f.id}`}
                                        checked={f.open}
                                        onChange={(v) => saveForm({ ...f, open: v }, v ? `"${f.name}" aberto para respostas.` : `"${f.name}" fechado.`)}
                                    />
                                    {f.id !== MAIN_FORM_ID && (
                                        <button
                                            data-testid={`form-delete-${f.id}`}
                                            onClick={() => deleteForm(f.id)}
                                            className="text-gray-500 transition-colors hover:text-red-400"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <input
                                data-testid="new-form-name-input"
                                value={newFormName}
                                onChange={(e) => setNewFormName(e.target.value)}
                                placeholder="Nome do novo formulário"
                                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-100 outline-none placeholder:text-gray-600 focus:border-violet-500/60"
                            />
                            <button
                                data-testid="create-form-button"
                                onClick={createForm}
                                className="flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/15 px-4 py-2 text-sm font-bold text-violet-200 transition-colors hover:bg-violet-500/25"
                            >
                                <Plus className="h-4 w-4" /> Criar formulário
                            </button>
                        </div>
                    </div>

                    {selected && (
                        <div className="glass-card rounded-2xl p-6">
                            <h2 className="font-display text-lg font-bold text-white">
                                Perguntas — {selected.name}
                            </h2>
                            <p className="mt-1 text-xs text-gray-500">
                                Edite títulos, ative/desative ou remova perguntas. Salve para aplicar.
                            </p>
                            <input
                                data-testid="form-name-input"
                                value={selected.name}
                                onChange={(e) => patchSelected({ name: e.target.value })}
                                className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-gray-100 outline-none focus:border-violet-500/60"
                            />
                            <input
                                data-testid="form-webhook-input"
                                value={selected.webhook_url || ""}
                                onChange={(e) => patchSelected({ webhook_url: e.target.value })}
                                placeholder="Webhook do Discord deste formulário (vazio = usa o principal)"
                                className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-gray-100 outline-none placeholder:text-gray-600 focus:border-violet-500/60"
                            />
                            <div className="mt-4 space-y-3">
                                {selected.questions.map((q) => (
                                    <div
                                        key={q.id}
                                        data-testid={`question-row-${q.id}`}
                                        className={`rounded-xl border p-4 transition-colors ${
                                            q.enabled ? "border-white/10 bg-white/[0.03]" : "border-white/5 bg-transparent opacity-50"
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-center gap-3">
                                            <input
                                                data-testid={`question-label-${q.id}`}
                                                value={q.label}
                                                onChange={(e) => updateQuestion(q.id, { label: e.target.value })}
                                                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-gray-100 outline-none focus:border-violet-500/60"
                                            />
                                            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                                {TYPE_LABELS[q.type]}
                                            </span>
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                                <Toggle
                                                    testid={`question-enabled-${q.id}`}
                                                    checked={q.enabled}
                                                    onChange={(v) => updateQuestion(q.id, { enabled: v })}
                                                />
                                                <span>Ativa</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                                <Toggle
                                                    testid={`question-required-${q.id}`}
                                                    checked={q.required}
                                                    onChange={(v) => updateQuestion(q.id, { required: v })}
                                                />
                                                <span>Obrigatória</span>
                                            </div>
                                            {!DEFAULT_IDS.includes(q.id) && (
                                                <button
                                                    data-testid={`question-delete-${q.id}`}
                                                    onClick={() =>
                                                        patchSelected({ questions: selected.questions.filter((x) => x.id !== q.id) })
                                                    }
                                                    className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-red-400 transition-colors hover:text-red-300"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" /> Remover
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-5 rounded-xl border border-dashed border-violet-500/30 bg-violet-500/5 p-4">
                                <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-violet-300">
                                    Adicionar pergunta
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <input
                                        data-testid="add-question-label"
                                        value={newLabel}
                                        onChange={(e) => setNewLabel(e.target.value)}
                                        placeholder="Título da nova pergunta"
                                        className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-100 outline-none placeholder:text-gray-600 focus:border-violet-500/60"
                                    />
                                    <select
                                        data-testid="add-question-type"
                                        value={newType}
                                        onChange={(e) => setNewType(e.target.value)}
                                        className="rounded-lg border border-white/10 bg-[#151824] px-3 py-2 text-sm text-gray-200 outline-none"
                                    >
                                        <option value="text">Texto curto</option>
                                        <option value="textarea">Texto longo</option>
                                        <option value="number">Número</option>
                                    </select>
                                    <button
                                        data-testid="add-question-button"
                                        onClick={addQuestion}
                                        className="flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/15 px-4 py-2 text-sm font-bold text-violet-200 transition-colors hover:bg-violet-500/25"
                                    >
                                        <Plus className="h-4 w-4" /> Adicionar
                                    </button>
                                </div>
                            </div>

                            <button
                                data-testid="save-questions-button"
                                onClick={() => saveForm(selected)}
                                disabled={saving}
                                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-violet-900/40 transition-all hover:scale-[1.01] disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Salvar formulário
                            </button>
                        </div>
                    )}
                </div>

                <div className="glass-card h-fit rounded-2xl p-6">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/25 bg-amber-500/10 text-amber-300">
                            <KeyRound className="h-4 w-4" />
                        </div>
                        <h2 className="font-display text-lg font-bold text-white">Trocar senha</h2>
                    </div>
                    <form onSubmit={changePassword} className="mt-5 space-y-3">
                        <input
                            data-testid="current-password-input"
                            type="password"
                            value={pwd.current}
                            onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                            placeholder="Senha atual"
                            required
                            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-100 outline-none placeholder:text-gray-600 focus:border-violet-500/60"
                        />
                        <input
                            data-testid="new-password-input"
                            type="password"
                            value={pwd.next}
                            onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                            placeholder="Nova senha (mín. 6 caracteres)"
                            required
                            minLength={6}
                            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-100 outline-none placeholder:text-gray-600 focus:border-violet-500/60"
                        />
                        {pwdNotice.text && (
                            <div
                                data-testid="password-notice"
                                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium ${
                                    pwdNotice.type === "ok"
                                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                                        : "border-red-500/30 bg-red-500/10 text-red-300"
                                }`}
                            >
                                {pwdNotice.type === "ok" ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                                {pwdNotice.text}
                            </div>
                        )}
                        <button
                            data-testid="change-password-button"
                            type="submit"
                            className="w-full rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-bold text-amber-200 transition-colors hover:bg-amber-500/20"
                        >
                            Alterar senha
                        </button>
                    </form>
                    <p className="mt-5 flex items-start gap-2 text-[11px] leading-relaxed text-gray-600">
                        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-400" />
                        Após 5 tentativas de login erradas, o acesso é bloqueado por 15 minutos. Cada visitante
                        só pode enviar 1 resposta por minuto.
                    </p>
                </div>
            </div>
        </PageShell>
    );
}
